import { useState, useEffect, useRef } from "react";
import { CreditCard, Smartphone, Hash, Copy, CheckCircle2, RefreshCw, Clock, XCircle, Download, ReceiptText } from "lucide-react";
import toast from "react-hot-toast";
import Modal from "@/components/modal";
import Button from "@/components/buttons/Button";
import TextInput from "@/components/form-controls/TextInput";
import SelectInput from "@/components/form-controls/SelectInput";
import {
    generateControlNumber,
    submitUssdPush,
    submitCardPayment,
    getPaymentStatus,
    downloadPaymentDocument,
    normalizeTzPhone,
    isValidTzPhone,
    type MobileProvider,
    type ZanCardType,
    type ZanPaymentStatus,
    type ZanMalipoResult,
    type UssdPushResult,
    type ZanCustomerFields,
} from "@/features/applications/services/applications-services";

type PaymentMode = 'control-number' | 'ussd-push' | 'card';

interface Props extends ZanCustomerFields {
    isOpen: boolean;
    onClose: () => void;
    amount: number;
    defaultPhoneNumber?: string;
    onPaymentConfirmed?: () => void;
}

const PROVIDERS: { value: MobileProvider; label: string }[] = [
    { value: 'MPESA', label: 'M-Pesa (Vodacom)' },
    // Tigo Pesa rebranded to Mixx by Yas; one wallet, one name.
    { value: 'TIGOPESA', label: 'Mixx by Yas' },
    { value: 'AIRTEL_MONEY', label: 'Airtel Money' },
    { value: 'HALOPESA', label: 'Halo Pesa' },
    { value: 'EAZYPESA', label: 'Eazy Pesa (Zantel)' },
    { value: 'TPESA', label: 'T-Pesa (TTCL)' },
];

const CARD_TYPES: { value: ZanCardType; label: string }[] = [
    { value: 'VISA', label: 'Visa' },
    { value: 'MASTERCARD', label: 'Mastercard' },
    { value: 'AMEX', label: 'American Express' },
    { value: 'UNIONPAY', label: 'UnionPay' },
];

const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const YEARS = Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() + i));

function detectProvider(phone: string): MobileProvider | '' {
    const digits = phone.replace(/\D/g, '');
    const suffix = digits.length === 12 ? digits.slice(3) : digits.length === 10 ? digits.slice(1) : null;
    if (!suffix) return '';
    const p3 = suffix.slice(0, 3);
    if (['074','075','076','077','078'].includes(p3)) return 'MPESA';
    if (['065','067','071'].includes(p3)) return 'TIGOPESA';
    if (['068','069'].includes(p3)) return 'AIRTEL_MONEY';
    if (p3 === '062') return 'HALOPESA';
    if (p3 === '079') return 'EAZYPESA';
    if (p3 === '041') return 'TPESA';
    return '';
}

const STATUS_LABELS: Record<ZanPaymentStatus, string> = {
    PENDING: 'Initializing payment…',
    PROCESSING: 'Waiting for payment confirmation…',
    PAID: 'Payment confirmed!',
    FAILED: 'Payment failed.',
    EXPIRED: 'Payment session expired.',
};

export default function InitiatePaymentModal({
    isOpen,
    onClose,
    amount,
    defaultPhoneNumber,
    onPaymentConfirmed,
    ...customerFields
}: Props) {
    const { custName, custEmail } = customerFields;
    const [step, setStep] = useState<0 | 1 | 2>(0);
    const [mode, setMode] = useState<PaymentMode>('ussd-push');

    // Mobile form — seeded from defaultPhoneNumber when supplied
    const [phoneNumber, setPhoneNumber] = useState('');
    const [provider, setProvider] = useState<MobileProvider | ''>('');

    useEffect(() => {
        if (!defaultPhoneNumber || !isOpen) return;
        const normalized = normalizeTzPhone(defaultPhoneNumber);
        if (!normalized) return;
        setPhoneNumber(normalized);
        const detected = detectProvider(normalized);
        if (detected) setProvider(detected);
    }, [defaultPhoneNumber, isOpen]);

    // Card form
    const [cardType, setCardType] = useState<ZanCardType>('VISA');
    const [cardNumber, setCardNumber] = useState('');
    const [cardHolderName, setCardHolderName] = useState('');
    const [cardExpiryMonth, setCardExpiryMonth] = useState('');
    const [cardExpiryYear, setCardExpiryYear] = useState('');
    const [cvv, setCvv] = useState('');

    // Result state
    const [controlNumber, setControlNumber] = useState<string | null>(null);
    const [pollingStatus, setPollingStatus] = useState<ZanPaymentStatus>('PROCESSING');
    const [payRefId, setPayRefId] = useState<string | null>(null);
    const [pushInitiated, setPushInitiated] = useState<boolean | null>(null);
    const [pushMessage, setPushMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    // True once auto-polling has run its course (~5 min) without a terminal
    // status — the UI drops the "waiting" spinner and offers a manual re-check.
    const [pollTimedOut, setPollTimedOut] = useState(false);
    const [isChecking, setIsChecking] = useState(false);

    const pollAttemptsRef = useRef(0);
    const terminalStatuses: ZanPaymentStatus[] = ['PAID', 'FAILED', 'EXPIRED'];

    // Fetch the status once and apply it. Returns the fetched status (or null).
    const applyStatus = async (): Promise<ZanPaymentStatus | null> => {
        if (!controlNumber) return null;
        try {
            const res = await getPaymentStatus(controlNumber);
            const status = (res as any)?.results?.status ?? (res as any)?.status;
            if (status) {
                setPollingStatus(status);
                const ref = (res as any)?.results?.payRefId ?? (res as any)?.payRefId;
                if (ref) setPayRefId(ref);
                if (terminalStatuses.includes(status) && onPaymentConfirmed && status === 'PAID') {
                    onPaymentConfirmed();
                }
                return status;
            }
        } catch {
            // ignore transient poll errors
        }
        return null;
    };

    useEffect(() => {
        if (step !== 2 || !controlNumber || pollTimedOut || terminalStatuses.includes(pollingStatus)) return;

        const interval = setInterval(async () => {
            if (pollAttemptsRef.current >= 30) {
                clearInterval(interval);
                // Ran out of automatic attempts without a terminal status — surface
                // an honest "still pending" state instead of spinning forever.
                setPollTimedOut(true);
                return;
            }
            pollAttemptsRef.current++;
            await applyStatus();
        }, 10_000);

        return () => clearInterval(interval);
    }, [step, controlNumber, pollingStatus, pollTimedOut]);

    // Manual re-check after the auto-poll times out: fetch once, and if still
    // non-terminal, restart the automatic polling loop.
    const handleCheckStatus = async () => {
        setIsChecking(true);
        try {
            const status = await applyStatus();
            if (!status || !terminalStatuses.includes(status)) {
                pollAttemptsRef.current = 0;
                setPollTimedOut(false);
            }
        } finally {
            setIsChecking(false);
        }
    };

    const handlePhoneChange = (value: string) => {
        const digits = value.replace(/\D/g, '');
        setPhoneNumber(digits);
        const detected = detectProvider(digits);
        if (detected) setProvider(detected);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const isMobile = mode === 'control-number' || mode === 'ussd-push';
            const normalizedPhone = normalizeTzPhone(phoneNumber);
            if (isMobile && !isValidTzPhone(normalizedPhone)) {
                setSubmitError('Phone number must be 12 digits starting with country code, e.g. 255712345678.');
                setIsSubmitting(false);
                return;
            }
            if (mode === 'control-number') {
                const res = await generateControlNumber({
                    amount,
                    phoneNumber: normalizedPhone,
                    provider: provider as MobileProvider,
                    ...customerFields,
                });
                const data: ZanMalipoResult = (res as any)?.results ?? res;
                setControlNumber(data.controlNumber);
                setPollingStatus(data.status);
            } else if (mode === 'ussd-push') {
                const res = await submitUssdPush({
                    amount,
                    phoneNumber: normalizedPhone,
                    provider: provider as MobileProvider,
                    ...customerFields,
                });
                const data: UssdPushResult = (res as any)?.results ?? res;
                setControlNumber(data.controlNumber);
                setPollingStatus(data.status);
                setPushInitiated(data.pushInitiated);
                setPushMessage(data.pushMessage);
            } else {
                const res = await submitCardPayment({
                    amount,
                    cardType,
                    cardNumber: cardNumber.replace(/\s/g, ''),
                    cardHolderName: cardHolderName.toUpperCase(),
                    cardExpiryMonth,
                    cardExpiryYear,
                    cvv,
                    email: custEmail,
                    ...customerFields,
                    // Cardholder name on the card wins over inherited custName
                    custName: cardHolderName ? cardHolderName.toUpperCase() : custName,
                });
                const data: ZanMalipoResult = (res as any)?.results ?? res;
                setControlNumber(data.controlNumber);
                setPollingStatus(data.status);
            }
            pollAttemptsRef.current = 0;
            setPollTimedOut(false);
            setStep(2);
        } catch (e: any) {
            setSubmitError(e?.response?.data?.message ?? e?.message ?? 'Payment failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopy = () => {
        if (!controlNumber) return;
        navigator.clipboard.writeText(controlNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownload = async (type: "invoice" | "receipt") => {
        if (!controlNumber) return;
        try {
            await downloadPaymentDocument(controlNumber, type);
        } catch (e: any) {
            toast.error(e?.message || "Failed to download document");
        }
    };

    const resetState = () => {
        setStep(0);
        setMode('ussd-push');
        setPhoneNumber('');
        setProvider('');
        setCardType('VISA');
        setCardNumber('');
        setCardHolderName('');
        setCardExpiryMonth('');
        setCardExpiryYear('');
        setCvv('');
        setControlNumber(null);
        setPollingStatus('PROCESSING');
        setPayRefId(null);
        setPushInitiated(null);
        setPushMessage(null);
        setSubmitError(null);
        setCopied(false);
        setPollTimedOut(false);
        setIsChecking(false);
        pollAttemptsRef.current = 0;
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const formatAmount = (n: number) => `TZS ${n.toLocaleString()}`;
    const isMobileMode = mode === 'control-number' || mode === 'ussd-push';
    const isTerminal = terminalStatuses.includes(pollingStatus);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="sm" title="Make Payment">
            {/* ── Step 0: Mode selection ── */}
            {step === 0 && (
                <div className="space-y-4">
                    <div className="text-center pb-2">
                        <p className="text-2xl font-bold text-gray-900">{formatAmount(amount)}</p>
                        <p className="text-sm text-gray-500 mt-1">Choose a payment method</p>
                    </div>

                    <div className="space-y-3">
                        {[
                            {
                                id: 'ussd-push' as PaymentMode,
                                label: 'Mobile Push',
                                sub: 'USSD/STK prompt sent to your phone',
                                icon: <Smartphone className="w-5 h-5" />,
                            },
                            {
                                id: 'control-number' as PaymentMode,
                                label: 'Control Number',
                                sub: 'Pay at any bank, agent, or mobile app',
                                icon: <Hash className="w-5 h-5" />,
                            },
                            {
                                id: 'card' as PaymentMode,
                                label: 'Credit / Debit Card',
                                sub: 'Visa, Mastercard, Amex, UnionPay',
                                icon: <CreditCard className="w-5 h-5" />,
                            },
                        ].map((opt) => (
                            <button
                                key={opt.id}
                                onClick={() => setMode(opt.id)}
                                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                                    mode === opt.id
                                        ? 'border-primary bg-primary-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                        mode === opt.id ? 'border-primary' : 'border-gray-300'
                                    }`}>
                                        {mode === opt.id && <div className="w-2 h-2 rounded-full bg-primary" />}
                                    </div>
                                    <div className={`p-2 rounded-lg flex-shrink-0 ${
                                        mode === opt.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {opt.icon}
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">{opt.label}</p>
                                        <p className="text-xs text-gray-500">{opt.sub}</p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    <Button intent="primary" size="md" className="w-full mt-2" onClick={() => setStep(1)}>
                        Continue
                    </Button>
                </div>
            )}

            {/* ── Step 1: Details form ── */}
            {step === 1 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between mb-1">
                        <button
                            onClick={() => setStep(0)}
                            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                            ← Back
                        </button>
                        <span className="text-sm font-semibold text-gray-700">{formatAmount(amount)}</span>
                    </div>

                    {/* Mobile form (used for both control-number and ussd-push) */}
                    {isMobileMode && (
                        <>
                            <TextInput
                                label="Phone Number"
                                placeholder="07XXXXXXXX or 255XXXXXXXXX"
                                value={phoneNumber}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                maxLength={12}
                                required
                            />
                            <SelectInput
                                label="Mobile Provider"
                                placeholder="Select provider"
                                options={PROVIDERS}
                                valueName="value"
                                displayName="label"
                                value={provider}
                                onChange={(e) => setProvider(e.target.value as MobileProvider)}
                                required
                            />
                            {mode === 'ussd-push' && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                                    A payment prompt will be sent to your phone. Enter your mobile money PIN to confirm.
                                </div>
                            )}
                            {mode === 'control-number' && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                                    A control number will be generated. Use it to pay at any bank, agent, or mobile app.
                                </div>
                            )}
                        </>
                    )}

                    {/* Card form */}
                    {mode === 'card' && (
                        <>
                            <SelectInput
                                label="Card Type"
                                options={CARD_TYPES}
                                valueName="value"
                                displayName="label"
                                value={cardType}
                                onChange={(e) => setCardType(e.target.value as ZanCardType)}
                            />
                            <TextInput
                                label="Card Number"
                                placeholder="0000 0000 0000 0000"
                                value={cardNumber}
                                maxLength={19}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
                                    setCardNumber(raw.replace(/(.{4})/g, '$1 ').trim());
                                }}
                                required
                            />
                            <TextInput
                                label="Cardholder Name"
                                placeholder="JOHN DOE"
                                value={cardHolderName}
                                onChange={(e) => setCardHolderName(e.target.value)}
                                required
                            />
                            <div className="grid grid-cols-3 gap-3">
                                <SelectInput
                                    label="Month"
                                    options={MONTHS}
                                    value={cardExpiryMonth}
                                    onChange={(e) => setCardExpiryMonth(e.target.value)}
                                    placeholder="MM"
                                    required
                                />
                                <SelectInput
                                    label="Year"
                                    options={YEARS}
                                    value={cardExpiryYear}
                                    onChange={(e) => setCardExpiryYear(e.target.value)}
                                    placeholder="YYYY"
                                    required
                                />
                                <TextInput
                                    label="CVV"
                                    type="password"
                                    placeholder={cardType === 'AMEX' ? '4 digits' : '3 digits'}
                                    value={cvv}
                                    maxLength={cardType === 'AMEX' ? 4 : 3}
                                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                                    required
                                />
                            </div>
                        </>
                    )}

                    {submitError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                            {submitError}
                        </div>
                    )}

                    <Button
                        intent="primary"
                        size="md"
                        className="w-full"
                        loading={isSubmitting}
                        onClick={handleSubmit}
                        disabled={
                            isMobileMode
                                ? !phoneNumber || !provider
                                : !cardNumber || !cardHolderName || !cardExpiryMonth || !cardExpiryYear || !cvv
                        }
                    >
                        {mode === 'control-number'
                            ? 'Generate Control Number'
                            : mode === 'ussd-push'
                            ? 'Send Payment Prompt'
                            : `Pay ${formatAmount(amount)}`}
                    </Button>
                </div>
            )}

            {/* ── Step 2: Polling / Result ── */}
            {step === 2 && (
                <div className="space-y-4">
                    {/* Control number display — always visible for mobile modes, or while processing for card */}
                    {controlNumber && (mode !== 'card' || !isTerminal) && (
                        <div className="bg-primary-50 border border-primary/20 rounded-xl p-5 text-center">
                            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Control Number</p>
                            <p className="text-3xl font-bold text-primary tracking-widest">{controlNumber}</p>
                            <div className="mt-3 flex items-center justify-center gap-4">
                                <button
                                    onClick={handleCopy}
                                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                                >
                                    <Copy className="w-3 h-3" />
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                                <button
                                    onClick={() => handleDownload("invoice")}
                                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                                >
                                    <Download className="w-3 h-3" />
                                    Download
                                </button>
                                {pollingStatus === 'PAID' && (
                                    <button
                                        onClick={() => handleDownload("receipt")}
                                        className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                                    >
                                        <ReceiptText className="w-3 h-3" />
                                        Receipt
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* USSD push status */}
                    {mode === 'ussd-push' && pushInitiated !== null && (
                        <div className={`rounded-lg p-3 text-sm border ${
                            pushInitiated
                                ? 'bg-amber-50 border-amber-200 text-amber-800'
                                : 'bg-gray-50 border-gray-200 text-gray-700'
                        }`}>
                            {pushInitiated
                                ? <>
                                    <p className="font-medium">Prompt sent to your phone</p>
                                    <p className="mt-0.5">Enter your mobile money PIN to confirm payment.</p>
                                    {pushMessage && <p className="mt-1 text-xs opacity-70">{pushMessage}</p>}
                                    {controlNumber && (
                                        <p className="mt-1 text-xs">
                                            No prompt received? Use control number <strong>{controlNumber}</strong> to pay manually.
                                        </p>
                                    )}
                                  </>
                                : <>
                                    <p className="font-medium">Prompt could not be sent automatically.</p>
                                    {controlNumber && (
                                        <p className="mt-0.5">Use control number <strong>{controlNumber}</strong> to pay manually.</p>
                                    )}
                                  </>
                            }
                        </div>
                    )}

                    {/* Control number mode instructions */}
                    {mode === 'control-number' && controlNumber && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 space-y-1">
                            <p className="font-medium">How to pay:</p>
                            <p>1. Open your mobile money or banking app</p>
                            <p>2. Select "Pay Bill" or "Lipa"</p>
                            <p>3. Enter the control number above</p>
                            <p>4. Pay <strong>{formatAmount(amount)}</strong></p>
                        </div>
                    )}

                    {/* Polling status indicator — while auto-polling is active */}
                    {!isTerminal && !pollTimedOut && (
                        <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <RefreshCw className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" />
                            <div>
                                <p className="text-sm text-gray-700 font-medium">{STATUS_LABELS[pollingStatus]}</p>
                                <p className="text-xs text-gray-400">Checking every 10 seconds…</p>
                            </div>
                        </div>
                    )}

                    {/* Auto-poll timed out without confirmation — honest pending state */}
                    {!isTerminal && pollTimedOut && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                            <div className="flex items-start gap-3">
                                <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-amber-800">Still pending</p>
                                    <p className="text-xs text-amber-700 mt-0.5">
                                        We haven't received confirmation yet. If you've already paid, it may take a few
                                        minutes to reflect. You can check again below.
                                    </p>
                                </div>
                            </div>
                            <Button
                                intent="secondary"
                                size="md"
                                className="w-full"
                                loading={isChecking}
                                leftIcon={<RefreshCw className="w-4 h-4" />}
                                onClick={handleCheckStatus}
                            >
                                Check status
                            </Button>
                        </div>
                    )}

                    {/* PAID */}
                    {pollingStatus === 'PAID' && (
                        <div className="text-center space-y-3 py-2">
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                                <CheckCircle2 className="w-7 h-7 text-green-600" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">Payment Confirmed</h3>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{formatAmount(amount)}</p>
                                {payRefId && (
                                    <p className="text-xs text-gray-500 mt-1">Receipt: <span className="font-mono font-medium">{payRefId}</span></p>
                                )}
                            </div>
                            <Button intent="primary" size="md" className="w-full" onClick={handleClose}>
                                Done
                            </Button>
                        </div>
                    )}

                    {/* FAILED */}
                    {pollingStatus === 'FAILED' && (
                        <div className="text-center space-y-3 py-2">
                            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                                <XCircle className="w-7 h-7 text-red-500" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">Payment Failed</h3>
                                <p className="text-sm text-gray-500 mt-1">The payment could not be processed.</p>
                            </div>
                            <div className="flex gap-2">
                                <Button intent="secondary" size="md" className="flex-1" onClick={() => setStep(1)}>
                                    Try Again
                                </Button>
                                <Button intent="primary" size="md" className="flex-1" onClick={handleClose}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* EXPIRED */}
                    {pollingStatus === 'EXPIRED' && (
                        <div className="text-center space-y-3 py-2">
                            <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mx-auto">
                                <Clock className="w-7 h-7 text-orange-500" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">Session Expired</h3>
                                <p className="text-sm text-gray-500 mt-1">Your payment session has expired.</p>
                            </div>
                            <div className="flex gap-2">
                                <Button intent="secondary" size="md" className="flex-1" onClick={() => { resetState(); setStep(0); }}>
                                    Start Over
                                </Button>
                                <Button intent="primary" size="md" className="flex-1" onClick={handleClose}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}
